/**
 * Pre-defined color configurations for inspection / DDS card pages.
 *
 * Each entry uses FULL Tailwind class names so the JIT compiler
 * can detect and generate them at build time — never use template
 * literals to compose class names dynamically.
 */

export interface InspectionColorSet {
	/** Top accent border on the Card wrapper */
	border: string
	/** `from-*` class used in the linear header background */
	headerBg: string
	/** Icon wrapper background */
	iconBg: string
	/** Icon foreground color */
	iconText: string
	/** Border on the sticky CardFooter */
	footerBorder: string
	/** Full className for the primary save button */
	saveBtn: string
	/** Full className for the photo upload drop-zone */
	photoZone: string
	/** Classes added to a card-select button when it is the active selection */
	optionActive: string
}

const grassGreen: InspectionColorSet = {
	border: 'border-t-kaefer-grass-green',
	headerBg: 'from-kaefer-grass-green/35',
	iconBg: 'bg-kaefer-grass-green',
	iconText: 'text-white',
	footerBorder: 'border-t-kaefer-grass-green/35',
	saveBtn: 'bg-kaefer-grass-green text-white hover:bg-kaefer-grass-green/90',
	photoZone:
		'border-kaefer-grass-green/60 bg-kaefer-grass-green/5 text-kaefer-grass-green hover:border-kaefer-grass-green hover:bg-kaefer-grass-green/10 hover:text-kaefer-forest-green',
	optionActive:
		'bg-kaefer-grass-green border-kaefer-grass-green text-white hover:bg-kaefer-grass-green/90',
}

const sunnyYellow: InspectionColorSet = {
	border: 'border-t-kaefer-sunny-yellow',
	headerBg: 'from-kaefer-sunny-yellow/35',
	iconBg: 'bg-kaefer-sunny-yellow',
	iconText: 'text-kaefer-dark-grey',
	footerBorder: 'border-t-kaefer-sunny-yellow/35',
	saveBtn:
		'bg-kaefer-sunny-yellow text-kaefer-dark-grey hover:bg-kaefer-sunny-yellow/90',
	photoZone:
		'border-kaefer-sunny-yellow/60 bg-kaefer-sunny-yellow/5 text-kaefer-sunny-yellow hover:border-kaefer-sunny-yellow hover:bg-kaefer-sunny-yellow/10 hover:text-kaefer-pumpkin-orange',
	optionActive:
		'bg-kaefer-sunny-yellow border-kaefer-sunny-yellow text-kaefer-dark-grey hover:bg-kaefer-sunny-yellow/90',
}

const honeyYellow: InspectionColorSet = {
	border: 'border-t-kaefer-honey-yellow',
	headerBg: 'from-kaefer-honey-yellow/35',
	iconBg: 'bg-kaefer-honey-yellow',
	iconText: 'text-kaefer-dark-grey',
	footerBorder: 'border-t-kaefer-honey-yellow/35',
	saveBtn:
		'bg-kaefer-honey-yellow text-kaefer-dark-grey hover:bg-kaefer-honey-yellow/90',
	photoZone:
		'border-kaefer-honey-yellow/60 bg-kaefer-honey-yellow/5 text-kaefer-honey-yellow hover:border-kaefer-honey-yellow hover:bg-kaefer-honey-yellow/10 hover:text-kaefer-pumpkin-orange',
	optionActive:
		'bg-kaefer-honey-yellow border-kaefer-honey-yellow text-kaefer-dark-grey hover:bg-kaefer-honey-yellow/90',
}

const sapphireBlue: InspectionColorSet = {
	border: 'border-t-kaefer-sapphire-blue',
	headerBg: 'from-kaefer-sapphire-blue/35',
	iconBg: 'bg-kaefer-sapphire-blue',
	iconText: 'text-white',
	footerBorder: 'border-t-kaefer-sapphire-blue/35',
	saveBtn:
		'bg-kaefer-sapphire-blue text-white hover:bg-kaefer-sapphire-blue/90',
	photoZone:
		'border-kaefer-sapphire-blue/60 bg-kaefer-sapphire-blue/5 text-kaefer-sapphire-blue hover:border-kaefer-sapphire-blue hover:bg-kaefer-sapphire-blue/10',
	optionActive:
		'bg-kaefer-sapphire-blue border-kaefer-sapphire-blue text-white hover:bg-kaefer-sapphire-blue/90',
}

const violet: InspectionColorSet = {
	border: 'border-t-kaefer-violet',
	headerBg: 'from-kaefer-violet/35',
	iconBg: 'bg-kaefer-violet',
	iconText: 'text-white',
	footerBorder: 'border-t-kaefer-violet/35',
	saveBtn: 'bg-kaefer-violet text-white hover:bg-kaefer-violet/90',
	photoZone:
		'border-kaefer-violet/60 bg-kaefer-violet/5 text-kaefer-violet hover:border-kaefer-violet hover:bg-kaefer-violet/10',
	optionActive:
		'bg-kaefer-violet border-kaefer-violet text-white hover:bg-kaefer-violet/90',
}

const mediumGrey: InspectionColorSet = {
	border: 'border-t-kaefer-medium-grey',
	headerBg: 'from-kaefer-medium-grey/35',
	iconBg: 'bg-kaefer-medium-grey',
	iconText: 'text-white',
	footerBorder: 'border-t-kaefer-medium-grey/35',
	saveBtn: 'bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90',
	photoZone:
		'border-kaefer-medium-grey/60 bg-kaefer-medium-grey/5 text-kaefer-medium-grey hover:border-kaefer-medium-grey hover:bg-kaefer-medium-grey/10',
	optionActive:
		'bg-kaefer-medium-grey border-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90',
}

const oceanBlue: InspectionColorSet = {
	border: 'border-t-kaefer-ocean-blue',
	headerBg: 'from-kaefer-ocean-blue/35',
	iconBg: 'bg-kaefer-ocean-blue',
	iconText: 'text-white',
	footerBorder: 'border-t-kaefer-ocean-blue/30',
	saveBtn: 'bg-kaefer-ocean-blue text-white hover:bg-kaefer-ocean-blue/90',
	photoZone:
		'border-kaefer-ocean-blue/60 bg-kaefer-ocean-blue/5 text-kaefer-ocean-blue hover:border-kaefer-ocean-blue hover:bg-kaefer-ocean-blue/10',
	optionActive:
		'bg-kaefer-ocean-blue border-kaefer-ocean-blue text-white hover:bg-kaefer-ocean-blue/90',
}

export const INSPECTION_COLORS = {
	grassGreen,
	sunnyYellow,
	honeyYellow,
	sapphireBlue,
	oceanBlue,
	violet,
	mediumGrey,
} as const
